import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AssetService } from 'src/@ccmc/services/asset.service';
import { DocumentConnectorService } from 'src/@ccmc/services/doc-connector.service';
import { CCMCSelectedFieldService } from 'src/@ccmc/services/selected-field.service';
import { AmplifyService } from 'src/@ccmc/services/amplify.service';
import {
  STATUS_MESSAGES,
  STATUS_VALUES,
  TRAILING_DOC_FAILED_MESSAGE,
  TRAILING_DOC_STATE
} from '../constants';
import * as trailingDocsUtil from '../trailing-docs.util';

@Component({
  selector: 'app-selected-loan',
  templateUrl: './selected-loan.component.html',
  styleUrls: ['./selected-loan.component.scss']
})
export class SelectedLoanComponent implements OnInit, OnDestroy {
  private _isAllBoxesChecked = false;
  private _settings: any = {};
  private selectedSub: Subscription;
  private dcSub: Subscription;
  private isNXTSoftUser = false;
  @Input() 
  dcDoc: any;
  selectedFile: any;
  selectedLoan: any;
  selectedLoanDisplayedColumns = [
    'checkBox',
    'fileName',
    'source',
    'destination',
    'trailing',
    'statusMessage'
  ];
  showDetailedStatusMessage = false;
  selectedLoanDataSource: any;
  unsubscribe: Subject<any> = new Subject();
  destinations: any;
  @Input() 
  checkedFiles: Map<string, any> = new Map<string, any>();
  @Output() 
  checkedFilesChanged: EventEmitter<Map<string, any>> = new EventEmitter<Map<string, any>>();

  @Input() 
  isLoanSelectVisible: boolean = false;
  @Output() 
  loanSelectVisibleChange = new EventEmitter<boolean>();

  @Input() 
  set settings(value: any) {
    this._settings = value;
    this.settingsChange.emit(this._settings);
  }

  get settings(): any {
    return this._settings;
  }

  @Output() 
  settingsChange = new EventEmitter<any>();

  @Input() 
  set isAllBoxesChecked(value: boolean) {
    this._isAllBoxesChecked = value;
    this.isAllBoxesCheckedChange.emit(this._isAllBoxesChecked);
  }

  get isAllBoxesChecked(): boolean {
    return this._isAllBoxesChecked;
  }

  @Output() 
  isAllBoxesCheckedChange = new EventEmitter<boolean>();

  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('filterSearch') filterSearchRef: ElementRef;
  
  constructor(
    private dcService: DocumentConnectorService,
    private assetService: AssetService,
    private dialog: MatDialog,
    private selectedFieldService: CCMCSelectedFieldService,
    private amplifyService: AmplifyService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  @HostListener('document:keydown', ['$event'])
  handleUndo(event: KeyboardEvent) {
    if (!this.isNXTSoftUser) {
      return;
    }
    if (event.ctrlKey && event.key === 'z') {
      this.showDetailedStatusMessage = !this.showDetailedStatusMessage;
      this.refreshTable();
    }
  }

  refreshTable() {
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    this.getData();
    this.isLoanSelectVisible = true;
    this.loanSelectVisibleChange.emit(true);
    this.validateNXTSoftUser();
  }

  isCheckBox(element: any) {
    if (this.checkedFiles.has(element.fileName)) {
      return true;
    }
    return false;
  }

  getCheckBoxToolTipMsg(element:any) {
    if (!this.getStatus(element)?.toLowerCase()?.includes('failed') || element.hash) {
      return "";
    }

    if (this.isAllBoxesChecked) {
      return "Right click to unselect all";
    } 
    return "Right click to select all";
  }

  onRightClick(event: any): void {
    event.preventDefault();
    if (!this.isAllBoxesChecked) {
      if (this.selectedLoan && this.selectedLoan.files) {
        for (const file of this.selectedLoan.files) {
          if (this.getStatus(file)?.toLowerCase()?.includes('failed')) {
            if (!this.checkedFiles.has(file.fileName)) {
              this.checkedFiles.set(file.fileName, file);
            }
          }
        }
      }
    } else {
      this.checkedFiles.clear();
    }
    
    this.isAllBoxesChecked = !this.isAllBoxesChecked;
  }

  onCheckboxChange(event: any, element: any) {
    if (event.checked) {
      if (!this.checkedFiles.has(element.fileName)) {
        this.checkedFiles.set(element.fileName, element);
      }
    } else {
      this.checkedFiles.delete(element.fileName);
    }
     this.checkedFilesChanged.emit(this.checkedFiles);
  }
  
  isSomeSelected(): boolean {
    return this.selectedLoanDataSource.data.length > 0 && this.selectedLoanDataSource.data.some((element: any) => 
      this.getStatus(element)?.toLowerCase()?.includes('failed') && 
      this.checkedFiles.has(element?.fileName)
    ) && !this.isAllBoxesChecked;
  }
  
  toggleSelectAll(checked: boolean): void {
    if (checked) {
      if (this.selectedLoan && this.selectedLoan.files) {
        for (const file of this.selectedLoan.files) {
          if (this.getStatus(file)?.toLowerCase()?.includes('failed')) {
            if (!this.checkedFiles.has(file.fileName)) {
              this.checkedFiles.set(file.fileName, file);
            }
          }
        }
      }
    } else {
      this.checkedFiles.clear();
    }
    
    this.isAllBoxesChecked = !this.isAllBoxesChecked;
  }

  private async validateNXTSoftUser() {
    if (await this.amplifyService.checkNXTsoftUserGroup()) {
      this.isNXTSoftUser = true;
    }
  }

  onSelectFile(selectedFile: any) {
    this.selectedFile = selectedFile;
  }

  /**
   * Puts the given file rows into the table.
   *
   * On a refresh of the loan already on screen the existing data source is reused
   * and only its rows are replaced. Swapping in a new MatTableDataSource makes the
   * table re-attach and rebuild every row, which would defeat the row keying and
   * close any tooltip the user is reading.
   *
   * @param rows - File rows to display.
   * @param reuseExisting - True when this is a refresh of the same loan.
   */
  private setSelectedLoanRows(rows: any[], reuseExisting: boolean): void {
    if (reuseExisting && this.selectedLoanDataSource) {
      this.selectedLoanDataSource.data = rows;
      return;
    }

    this.selectedLoanDataSource = new MatTableDataSource(rows);
    // initializes sort
    this.selectedLoanDataSource.sort = this.sort;
    this.selectedLoanDataSource.sortingDataAccessor = (item: any, property: any) => {
      switch (property) {
        case 'fileName': return new String(item.fileName);
        case 'source': return new String(item.source);
        case 'destination': return new String(item.destination);
        case 'statusMessage': return new String(item.statusMessage);
        case 'trailing': return item.updatedAt ? new Date(item.updatedAt).getTime() : 0;
        default: return item[property];
      }
    };
  }

  /**
   * Identifies file rows for change detection.
   *
   * This table is rebuilt whenever the open loan is refreshed, in the background or
   * by the refresh button. Without a stable key every row is destroyed and
   * recreated, which closes any tooltip the user is reading and drops hover on the
   * row under the cursor.
   *
   * The key has to be more than the file name. The same name legitimately appears
   * on several rows: a loan document and its trailing counterpart, or the same file
   * recorded in two different trailing buckets. Keying on the name alone produced
   * duplicate keys, and Angular cannot tell those rows apart, so a refresh applied
   * updates to the wrong row or appeared to do nothing at all.
   *
   * Bucket and source are stable for the life of a row, unlike the status message
   * or `updatedAt`, so including them distinguishes the rows without making the key
   * change every time the row's contents do.
   *
   * @param _index - Row index, unused.
   * @param row - The row's file record.
   * @returns A stable identity for the row.
   */
  trackByFile = (_index: number, row: any): any => {
    if (!row) {
      return row;
    }
    return `${row.trailingState ?? 'loan'}|${row.source ?? ''}|${row.fileName ?? ''}`;
  };

  getData() {
    this.selectedSub = this.selectedFieldService.onHistoryLoanFieldSelected
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(selected => {
        console.log('Selected History Loan', selected);

        // The background fetch re-emits the open loan whenever its files change,
        // so this also fires as a refresh of the loan already on screen. Only
        // reset the filter when the user actually switched loans, otherwise a
        // search would be wiped every few seconds while a transfer runs.
        const previousLoanGUID = this.selectedLoan?.loanGUID;
        const isSameLoan = selected?.loanGUID !== undefined
          && selected?.loanGUID === previousLoanGUID;
        this.selectedLoan = selected;

        if (!isSameLoan) {
          this.clearSearchData();
        }

        if (this.selectedLoan && this.selectedLoan.files) {
          // Standard documents
          let tempArray = JSON.parse(JSON.stringify(this.selectedLoan.files));
          for (let loan of tempArray) {
            let pathArray = loan.S3FileLocation.split('/');
            if (pathArray.length >= 5) {
              loan.destination = pathArray[pathArray.length - 3] + '/' + pathArray[pathArray.length - 2];
              loan.source = pathArray[pathArray.length - 3] + '/' + pathArray[pathArray.length - 2];
            }
          }

          // Trailing documents. Each row is tagged with the bucket it came from,
          // because the bucket is what actually records the outcome. Without it
          // these rows were indistinguishable from loan documents and fell through
          // to the message-based rules in getStatus, which report anything that is
          // not an explicit success as a failed transfer.
          if (this.selectedLoan.trailingDocs) {
            for (const state of [
              TRAILING_DOC_STATE.FAILED,
              TRAILING_DOC_STATE.IN_PROCESS,
              TRAILING_DOC_STATE.PROCESSED
            ]) {
              tempArray.push(
                ...this.buildTrailingDocRows(this.selectedLoan.trailingDocs[state], state)
              );
            }
          }

          this.setSelectedLoanRows(tempArray, isSameLoan);
        } else {
          this.setSelectedLoanRows([], isSameLoan);
        }
      });

    this.dcSub = this.dcService.dcDocument
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(dcDocument => {
        if (dcDocument) {
          if (dcDocument.destinations) {
            this.destinations = dcDocument.destinations;
          }
        } else {
          this.destinations = undefined;
        }
      });
  }

  isRetry(element: any): boolean {
    const retryTimestampStr = element?.retryTimestamp;
    if (retryTimestampStr) { 
      const retryTimestamp = Number(retryTimestampStr); // Convert string to number
      if (!isNaN(retryTimestamp)) { // Ensure the conversion was successful
        const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
        return retryTimestamp > fiveMinutesAgo; // Return true if it's within the last 5 minutes
      }
    }
    return false; 
  }

  /**
   * Builds detail rows for one trailing documents bucket, tagged with that bucket.
   *
   * @param bucket - The trailingDocs bucket contents, keyed by file name.
   * @param state - Which bucket it is, from TRAILING_DOC_STATE.
   * @returns Rows ready to append to the detail table.
   */
  private buildTrailingDocRows(bucket: any, state: string): any[] {
    if (!bucket) {
      return [];
    }

    return Object.values(bucket).map((content: any) => {
      const row = { ...content, trailingState: state, isTrailingDoc: true };

      if (row.S3FileLocation) {
        const pathArray = row.S3FileLocation.split('/');
        if (pathArray.length >= 5) {
          const path = pathArray[pathArray.length - 3] + '/' + pathArray[pathArray.length - 2];
          row.destination = path;
          row.source = path;
        }
      }

      return row;
    });
  }

  /**
   * Whether a trailing document has sat in the in-process bucket long enough to be
   * treated as stuck.
   *
   * Shares its rules with the history table through trailing-docs.util, so a row
   * cannot report differently here than in the tallies above it.
   *
   * @param element - A detail row.
   * @returns True when the row is in process and has stopped moving.
   */
  isStuckInProcess(element: any): boolean {
    if (element?.trailingState !== TRAILING_DOC_STATE.IN_PROCESS) {
      return false;
    }
    return trailingDocsUtil.isStalled(element, this.selectedLoan?.date);
  }

  /**
   * Status for a trailing document row, decided by the bucket it sits in.
   *
   * Kept out of the message-based rules below, which exist for loan documents and
   * report anything that is not an explicit success as a failed transfer. Applied
   * to trailing documents that rule marked every in-process document as failed,
   * because their messages differ and because the one hour cutoff it uses is
   * shorter than a trailing run legitimately takes.
   *
   * @param element - A detail row.
   * @returns The status to display.
   */
  private getTrailingDocStatus(element: any): string {
    const statusMessage = element.statusMessage || '';

    switch (element.trailingState) {
      case TRAILING_DOC_STATE.FAILED:
        // Prefer the recorded reason, falling back to the generic message when the
        // entry carries no explanation of its own.
        return trailingDocsUtil.readsAsFailure(element) ? statusMessage : TRAILING_DOC_FAILED_MESSAGE;

      case TRAILING_DOC_STATE.PROCESSED:
        // Finished. Honour an explicit failure, since a failure can be recorded
        // without the file being moved out of this bucket.
        return statusMessage;

      case TRAILING_DOC_STATE.IN_PROCESS:
        // Still working reports its own message. Once it stops moving it is a
        // failure: the transfer window has passed and nothing is going to advance
        // it. The tooltip explains which of the two this is.
        return this.isStuckInProcess(element)
          ? TRAILING_DOC_FAILED_MESSAGE
          : statusMessage;

      default:
        return statusMessage;
    }
  }

  getStatus(element: any): string {
    if (element?.isTrailingDoc) {
      return this.getTrailingDocStatus(element);
    }

    const statusMessage = element.statusMessage || "";

    const lowerCaseStatusMessage = statusMessage.toLowerCase();
    // Additional conditions for checking specific messages after one hour
    const isStatusAllowedAfterHour =
    lowerCaseStatusMessage.includes(STATUS_MESSAGES.LOAN_FILE_EXPORTED);

    if (this.isRetry(element) && !lowerCaseStatusMessage.includes(STATUS_MESSAGES.LOAN_FILE_EXPORTED) && !lowerCaseStatusMessage.includes("fail")) {
      return "Retrying File Transfer";
    }
  
    // Check if the status message matches the relevant conditions
    const isRelevantMessage =
      lowerCaseStatusMessage.includes(STATUS_MESSAGES.DOCUMENT_RETRIEVED) ||
      lowerCaseStatusMessage.includes(STATUS_MESSAGES.PROCESSED_FILE) ||
      lowerCaseStatusMessage.includes(STATUS_MESSAGES.SENDING_FILE);
  
    // Convert selectedLoan.date to a Date object if it's a string, or use it directly if it's a timestamp
    const loanDateInMs = typeof this.selectedLoan.date === 'string' ? new Date(this.selectedLoan.date).getTime() : this.selectedLoan.date;
    const oneHourAgo = Date.now() - 60 * 60 * 1000; // Calculate the time one hour ago in milliseconds
  
    // Check if the loan date is older than one hour
    const isOlderThanOneHour = loanDateInMs < oneHourAgo;

    if (isStatusAllowedAfterHour) {
      return statusMessage;
    }

    // Determine the status to return
    if (!isRelevantMessage || isOlderThanOneHour && isRelevantMessage || this.selectedLoan?.status == STATUS_VALUES.UNSUCCESSFUL) {
      return 'Failed to transfer loan file. Please review logs for more details.';
    }

    return statusMessage;
  }

  getDetailedStatus(element: any): string {
    if (this.showDetailedStatusMessage) {
      return "";
    }

    // Trailing documents explain themselves by bucket, and this is shown to
    // everyone rather than internal users only: "in process" on its own does not
    // tell you whether to keep waiting.
    if (element?.isTrailingDoc) {
      return this.getTrailingDocDetailedStatus(element);
    }

    if (!this.isNXTSoftUser || this.isRetry(element) && !this.getStatus(element).toLowerCase().includes("fail")) {
      return "";
    }

    const statusMessage = element.statusMessage || "";
    const lowerCaseStatusMessage = statusMessage.toLowerCase();
  
    // Check if the status message matches the relevant conditions
    const isRelevantMessage =
      lowerCaseStatusMessage.includes(STATUS_MESSAGES.DOCUMENT_RETRIEVED) ||
      lowerCaseStatusMessage.includes(STATUS_MESSAGES.PROCESSED_FILE) ||
      lowerCaseStatusMessage.includes(STATUS_MESSAGES.SENDING_FILE);

    if (lowerCaseStatusMessage.includes(STATUS_MESSAGES.LOAN_FILE_EXPORTED) || lowerCaseStatusMessage.includes('please review logs')) {
      return "";
    }
  
    // Convert selectedLoan.date to a Date object if it's a string, or use it directly if it's a timestamp
    const loanDateInMs = typeof this.selectedLoan.date === 'string' ? new Date(this.selectedLoan.date).getTime() : this.selectedLoan.date;
    const oneHourAgo = Date.now() - 60 * 60 * 1000; // Calculate the time one hour ago in milliseconds
  
     // Check if the loan date is older than one hour
     const isOlderThanOneHour = loanDateInMs < oneHourAgo;

     if (!isRelevantMessage) {
      return statusMessage;
     }

    if (!isRelevantMessage || isOlderThanOneHour && isRelevantMessage || this.selectedLoan?.status == STATUS_VALUES.UNSUCCESSFUL) {
      return `Failed to update DynamoDB or the Lambda function may have timed out. Last recorded status message: "${statusMessage}"`;
    }
  
    return "";
  }

  /**
   * Tooltip for a trailing document row, explaining what its bucket means.
   *
   * @param element - A detail row.
   * @returns Tooltip text, or an empty string when the row needs no explanation.
   */
  private getTrailingDocDetailedStatus(element: any): string {
    switch (element.trailingState) {
      case TRAILING_DOC_STATE.FAILED:
        return 'This trailing document failed and cannot be retried from this '
          + 'screen. It needs manual follow-up.';

      case TRAILING_DOC_STATE.IN_PROCESS:
        if (this.isStuckInProcess(element)) {
          // The distinction that matters operationally: still working, or stopped.
          // Named as stuck rather than as a plain failure so support knows the
          // document was left mid-transfer rather than rejected by the core.
          return 'This trailing document is still marked in process but has not '
            + 'been updated in over 10 minutes, so the run is stuck. It cannot be '
            + 'retried from this screen and needs manual follow-up. Last recorded '
            + `status: "${element.statusMessage ?? 'none'}".`;
        }
        return 'This trailing document is still being processed.';

      case TRAILING_DOC_STATE.PROCESSED:
        // A failure recorded without the file being moved out of processed. Say so
        // rather than letting the bucket imply it finished cleanly.
        return trailingDocsUtil.readsAsFailure(element)
          ? 'This trailing document reported a failure. It needs manual follow-up.'
          : '';

      default:
        return '';
    }
  }

  goToSelectedLoansDestination(selectedFile: any) {
    for (let destination of this.destinations) {
      if (selectedFile.destination === destination.id) {
        this.selectedFieldService.onDestinationFieldSelected.next(destination);
        this.router.navigate(['/doc-connector/source-and-destination']);
      }
    }
  }

  trailingDates(trailingDate: any) {
    return this.dcService.trailingDates(trailingDate);
  }

  /**
   * Applies filter to the selected loan files data source
   * @param filterValue - The search term to filter by
   */
  applyFilter(filterValue: string) {
    if (this.selectedLoanDataSource) {
      const trimmedValue = filterValue.trim().toLowerCase();
      this.selectedLoanDataSource.filter = trimmedValue;
    }
  }

  /**
   * Clears the filter search
   */
  clearSearchData() {
    // Clear the input field value
    if (this.filterSearchRef && this.filterSearchRef.nativeElement) {
      this.filterSearchRef.nativeElement.value = '';
    }
    
    // Clear the data source filter
    if (this.selectedLoanDataSource) {
      this.selectedLoanDataSource.filter = '';
    }
  }

  ngOnDestroy() {
    // Clear the filter when exiting the component
    this.clearSearchData();
    
    // Clean up subscriptions
    this.unsubscribe.next(0);
    this.unsubscribe.complete();
  }
}
